import { gql } from 'apollo-angular';

export const MAPS_ACCESS_VALIDATE = gql`
    mutation MapsAccessValidate($accessKey: String!) {
        mapsAccessValidate(accessKey: $accessKey) {
            companyName
            expiresAt
            isAdmin
        }
    }
`;

export const MAPS_SESSION_VALIDATE = gql`
    query MapsSessionValidate {
        mapsSessionValidate {
            valid
            companyName
            isAdmin
            showPipelinePanelToAll
            defaultResultCount
            showCareerStage
            contactSendCap
        }
    }
`;

export const MAPS_SETTINGS = gql`
    query MapsSettings {
        mapsSettings {
            tierStrong
            tierMedium
            defaultResultCount
            relevanceCutoff
            showPipelinePanelToAll
            rerankProvider
            blendAlpha
            publicationTextWordLimit
            relevantPublicationCount
            careerStageSource
            showCareerStage
            hardDisqualify
            levelDisqualify
            educationDisqualify
            qualMultiplierVeryImportant
            qualMultiplierImportant
            qualMultiplierNiceToHave
            interdiscMaxPubs
            feasibilityEnabled
            feasibilityWeight
            orgCountryInference
            contactDeliveryMode
            contactFromAddress
            contactFromName
            contactRedirectAddress
            contactRecipientAllowlist
            contactSendCap
            contactWebhookPublicKey
        }
    }
`;

export const MAPS_SETTINGS_UPDATE = gql`
    mutation MapsSettingsUpdate($input: MapsSettingsInput!) {
        mapsSettingsUpdate(input: $input) {
            tierStrong
            tierMedium
            defaultResultCount
            relevanceCutoff
            showPipelinePanelToAll
            rerankProvider
            blendAlpha
            publicationTextWordLimit
            relevantPublicationCount
            careerStageSource
            showCareerStage
            hardDisqualify
            levelDisqualify
            educationDisqualify
            qualMultiplierVeryImportant
            qualMultiplierImportant
            qualMultiplierNiceToHave
            interdiscMaxPubs
            feasibilityEnabled
            feasibilityWeight
            orgCountryInference
            contactDeliveryMode
            contactFromAddress
            contactFromName
            contactRedirectAddress
            contactRecipientAllowlist
            contactSendCap
            contactWebhookPublicKey
        }
    }
`;

export const MAPS_LOGOUT = gql`
    mutation MapsLogout {
        mapsLogout {
            success
        }
    }
`;

export const MAPS_UPLOAD_URL_CREATE = gql`
    mutation MapsUploadUrlCreate($input: MapsUploadUrlInput!) {
        mapsUploadUrlCreate(input: $input) {
            uploadId
            uploadUrl
            blobPath
        }
    }
`;

export const MAPS_UPLOAD_FINALIZE = gql`
    mutation MapsUploadFinalize($uploadId: ID!) {
        mapsUploadFinalize(uploadId: $uploadId) {
            uploadId
            originalFileName
            contentType
            sizeBytes
            extractStatus
            extractedLength
            extractError
        }
    }
`;

export const MAPS_EXPORT_CREATE = gql`
    mutation MapsExportCreate($input: MapsExportInput!) {
        mapsExportCreate(input: $input) {
            downloadUrl
            blobPath
            expiresAt
        }
    }
`;

export const MAPS_ADMIN_DASHBOARD = gql`
    query MapsAdminDashboard {
        mapsAdminDashboard {
            companyId
            companyName
            activeTokenCount
            totalSearches
            searches30d
            avgLatencyMs
            lastSearchAt
            aiCost30d
            exports30d
            anySoftCapExceeded
        }
    }
`;

export const MAPS_SUMMARIES_GENERATE = gql`
    mutation MapsSummariesGenerate($searchId: ID!) {
        mapsSummariesGenerate(searchId: $searchId) {
            summaries {
                candidateId
                whySuggested
                thingsToLookInto
                cacheHit
                error
            }
            meta {
                generated
                cached
                errors
                latencyMs
            }
        }
    }
`;

// Shared selection for the full search payload. Consumed by the async
// status poll (mapsSearchStatus.result); the legacy single-request
// MAPS_SEARCH mutation was replaced by MAPS_SEARCH_START + MAPS_SEARCH_STATUS
// because Azure App Service cuts client connections at ~240s while cold
// searches run longer.
const MAPS_CANDIDATE_FIELDS = gql`
    fragment MapsCandidateFields on MapsCandidate {
        candidateId
        source
        firstName
        lastName
        organization
        jobTitle
        location {
            city
            state
            country
        }
        careerStage
        displayCareerStage
        publicationCount
        publicationsPerResearchYear
        orcid
        linkedInUrl
        aguProfileUrl
        email
        score
        matchTier
        relevantPublication {
            title
            journal
            publishedDate
            abstractPreview
            fullPaperUrl
        }
        whySuggested
        thingsToLookInto
        fitFlags
        verifiedFacts
        verifiedMatch
        recruitingFeasibility {
            score
            note
        }
        employmentHistory {
            jobTitle
            organization
            startDate
            endDate
            isCurrent
        }
    }
`;

const MAPS_BRIEF_FIELDS = gql`
    fragment MapsBriefFields on MapsSearchBrief {
        researchTopics
        retrievalQueries
        hardRequirements
        softPreferences
        exclusions
        unverifiable
        seeksInterdisciplinarity
        minYearsExperience
        targetCareerStages
        officeLocation {
            city
            region
            country
        }
        locationConstraint {
            onsite
            country
            visaSponsorship
            citizenshipCountry
        }
    }
`;

const MAPS_QUALIFICATION_FIELDS = gql`
    fragment MapsQualificationFields on MapsQualification {
        id
        text
        classification
        tier
        source
        removed
        gating
        verifiableFact
        universeCount
    }
`;

const MAPS_SEARCH_RESULT_FIELDS = gql`
    fragment MapsSearchResultFields on MapsSearchResult {
            searchId
            meta {
                latencyMs
                resultCount
            }
            parsedGuidance {
                stronglyPrioritized
                preferred
                exclusionary
                raw
            }
            brief {
                ...MapsBriefFields
            }
            trace {
                briefInput
                rerankGuidance
                rerankRoster
                rerankSource
                rerankLatencyMs
                candidates {
                    candidateId
                    name
                    cosine
                    matched
                    relevance
                    reason
                    fitFlags
                    summaryProfile
                }
                disqualified {
                    candidateId
                    name
                    reasons
                    rule
                }
                qualificationExcluded {
                    candidateId
                    name
                    reasons
                }
                poolTotal
                verifiedUniverse
                verifiedFilters
                poolSize
                rosterSize
                levelGateEnabled
                levelGateRoleLevel
            }
            qualifications {
                ...MapsQualificationFields
            }
            results {
                ...MapsCandidateFields
            }
    }
    ${MAPS_CANDIDATE_FIELDS}
    ${MAPS_BRIEF_FIELDS}
    ${MAPS_QUALIFICATION_FIELDS}
`;

export const MAPS_SEARCH_START = gql`
    mutation MapsSearchStart($input: MapsSearchInput!) {
        mapsSearchStart(input: $input) {
            searchRequestId
        }
    }
`;

export const MAPS_SEARCH_STATUS = gql`
    ${MAPS_SEARCH_RESULT_FIELDS}
    query MapsSearchStatus($searchRequestId: ID!) {
        mapsSearchStatus(searchRequestId: $searchRequestId) {
            searchRequestId
            status
            stage
            error {
                code
                message
            }
            result {
                ...MapsSearchResultFields
            }
        }
    }
`;

// ---- saved searches (STN client round 2026-08) ----

export const MAPS_SAVED_SEARCHES = gql`
    query MapsSavedSearches($limit: Int, $offset: Int) {
        mapsSavedSearches(limit: $limit, offset: $offset) {
            totalCount
            items {
                searchId
                title
                timestamp
                resultCount
            }
        }
    }
`;

export const MAPS_SEARCH_RESTORE = gql`
    query MapsSearchRestore($searchId: ID!) {
        mapsSearchRestore(searchId: $searchId) {
            searchId
            title
            timestamp
            jobDescription
            results {
                ...MapsCandidateFields
            }
            parsedGuidance {
                stronglyPrioritized
                preferred
                exclusionary
                raw
            }
            qualifications {
                ...MapsQualificationFields
            }
            brief {
                ...MapsBriefFields
            }
        }
    }
    ${MAPS_CANDIDATE_FIELDS}
    ${MAPS_QUALIFICATION_FIELDS}
    ${MAPS_BRIEF_FIELDS}
`;

export const MAPS_SEARCH_TITLE_UPDATE = gql`
    mutation MapsSearchTitleUpdate($searchId: ID!, $title: String!) {
        mapsSearchTitleUpdate(searchId: $searchId, title: $title) {
            searchId
            title
            timestamp
            resultCount
        }
    }
`;

// ---- partner links (admin: companies + magic-link credentials) --------------

const MAPS_PARTNER_COMPANY_FIELDS = gql`
    fragment MapsPartnerCompanyFields on MapsPartnerCompany {
        companyId
        companyName
        invites {
            inviteId
            label
            email
            roleName
            status
            expiresAt
            maxUses
            useCount
            createdAt
            legacyTokenScoped
        }
    }
`;

export const MAPS_PARTNER_COMPANIES = gql`
    query MapsPartnerCompanies {
        mapsPartnerCompanies {
            ...MapsPartnerCompanyFields
        }
    }
    ${MAPS_PARTNER_COMPANY_FIELDS}
`;

export const MAPS_PARTNER_COMPANY_CREATE = gql`
    mutation MapsPartnerCompanyCreate($name: String!) {
        mapsPartnerCompanyCreate(name: $name) {
            ...MapsPartnerCompanyFields
        }
    }
    ${MAPS_PARTNER_COMPANY_FIELDS}
`;

export const MAPS_PARTNER_LINK_CREATE = gql`
    mutation MapsPartnerLinkCreate($input: MapsPartnerLinkCreateInput!) {
        mapsPartnerLinkCreate(input: $input) {
            inviteId
            rawToken
            name
            email
            roleName
            expiresAt
            maxUses
        }
    }
`;

export const MAPS_PARTNER_LINK_RENAME = gql`
    mutation MapsPartnerLinkRename($inviteId: ID!, $name: String!) {
        mapsPartnerLinkRename(inviteId: $inviteId, name: $name) {
            success
        }
    }
`;

export const MAPS_PARTNER_LINK_ROTATE = gql`
    mutation MapsPartnerLinkRotate($inviteId: ID!) {
        mapsPartnerLinkRotate(inviteId: $inviteId) {
            inviteId
            rawToken
            name
            email
            roleName
            expiresAt
            maxUses
            rotatedFromInviteId
        }
    }
`;

export const MAPS_PARTNER_LINK_REVOKE = gql`
    mutation MapsPartnerLinkRevoke($inviteId: ID!) {
        mapsPartnerLinkRevoke(inviteId: $inviteId) {
            success
        }
    }
`;

export const MAPS_PARTNER_LINK_HIDE = gql`
    mutation MapsPartnerLinkHide($inviteId: ID!) {
        mapsPartnerLinkHide(inviteId: $inviteId) {
            success
        }
    }
`;

// ---- contact / messaging (2026-08 finish line) ----

export const MAPS_CONTACT_SEND = gql`
    mutation MapsContactSend($input: MapsContactSendInput!) {
        mapsContactSend(input: $input) {
            batchId
            deliveryMode
            recipients {
                candidateId
                email
                contactMessageId
                status
                error
            }
        }
    }
`;

export const MAPS_CONTACT_STATUS = gql`
    query MapsContactStatus($candidateIds: [ID!]!) {
        mapsContactStatus(candidateIds: $candidateIds) {
            candidateId
            lastStatus
            lastContactedAt
            contactCount
        }
    }
`;

export const MAPS_CONTACT_HISTORY = gql`
    query MapsContactHistory($limit: Int, $offset: Int) {
        mapsContactHistory(limit: $limit, offset: $offset) {
            totalCount
            items {
                contactMessageId
                batchId
                searchId
                candidateId
                recipientName
                recipientEmail
                subject
                status
                deliveryMode
                createdAt
                deliveredAt
                openedAt
                openCount
                clickCount
                bounceReason
            }
        }
    }
`;

// ---- admin Email Activity (dashboard section: drilldown + event export) ----

export const MAPS_ADMIN_EMAIL_ACTIVITY = gql`
    query MapsAdminEmailActivity($filter: MapsEmailActivityFilter, $groupBy: MapsEmailActivityGroupBy!) {
        mapsAdminEmailActivity(filter: $filter, groupBy: $groupBy) {
            key
            label
            secondary
            messages
            sent
            delivered
            opened
            clicked
            bounced
            failed
            lastActivityAt
        }
    }
`;

export const MAPS_ADMIN_EMAIL_MESSAGES = gql`
    query MapsAdminEmailMessages($filter: MapsEmailActivityFilter, $limit: Int, $offset: Int) {
        mapsAdminEmailMessages(filter: $filter, limit: $limit, offset: $offset) {
            totalCount
            items {
                contactMessageId
                companyId
                companyName
                batchId
                replyTo
                recipientName
                recipientEmail
                subject
                status
                deliveryMode
                redirectedTo
                createdAt
                sentAt
                deliveredAt
                openedAt
                openCount
                clickCount
                bounceReason
            }
        }
    }
`;

export const MAPS_ADMIN_EMAIL_MESSAGE_DETAIL = gql`
    query MapsAdminEmailMessageDetail($id: ID!) {
        mapsAdminEmailMessageDetail(id: $id) {
            message {
                contactMessageId
                companyId
                companyName
                batchId
                replyTo
                recipientName
                recipientEmail
                subject
                status
                deliveryMode
                redirectedTo
                createdAt
                sentAt
                deliveredAt
                openedAt
                openCount
                clickCount
                bounceReason
            }
            body
            errorMessage
            sgMessageId
            inviteId
            userId
            linkName
            userName
            events {
                id
                eventType
                occurredAt
                url
                reason
            }
        }
    }
`;

export const MAPS_ADMIN_EMAIL_TIMELINE = gql`
    query MapsAdminEmailTimeline($filter: MapsEmailActivityFilter) {
        mapsAdminEmailTimeline(filter: $filter) {
            date
            delivered
            opened
            clicked
        }
    }
`;

export const MAPS_ADMIN_EMAIL_EVENTS_EXPORT = gql`
    mutation MapsAdminEmailEventsExport($input: MapsEmailEventsExportInput!) {
        mapsAdminEmailEventsExport(input: $input) {
            downloadUrl
            blobPath
            expiresAt
        }
    }
`;
